import axios, { AxiosInstance } from 'axios';
import { 
  Project, 
  Skill, 
  ContactMessage, 
  Experience, 
  Education,
  CommunityPlatform,
  BlogPost,
  Certification,
  Testimonial,
  Service,
  Resource,
  TimelineEvent,
  FAQ
} from '../types';

// ==============================
// Axios (kept for future use)
// ==============================

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// ==============================
// Helpers (Promise-based, no async keyword)
// ==============================

const fetchJson = <T>(route: string): Promise<{ data: T }> => {
  return fetch(`/data/${route}`)
    .then((res) => res.json())
    .then((data) => ({ data }));
};

const findById = <T extends { id: string }>(items: T[], id: string): T => {
  const item = items.find((x) => x.id === id);
  if (!item) throw new Error('Item not found');
  return item;
};

const findByField = <T>(
  items: T[],
  field: keyof T,
  value: any
): T => {
  const item = items.find((x) => x[field] === value);
  if (!item) throw new Error('Item not found');
  return item;
};

// ==============================
// Types export
// ==============================

export type { 
  Project, 
  Skill, 
  ContactMessage, 
  Experience, 
  Education,
  CommunityPlatform,
  BlogPost,
  Certification,
  Testimonial,
  Service,
  Resource,
  TimelineEvent,
  FAQ
};

// ==============================
// APIs
// ==============================

export const projectsAPI = {
  getAll: (): Promise<{ data: Project[] }> =>
    fetchJson<Project[]>('projects.json'), // api.get('/projects'),

  getById: (id: string): Promise<{ data: Project }> =>
    fetchJson<Project[]>('projects.json').then(({ data }) => ({
      data: findById(data, id),
    })), // api.get(`/projects/${id}`)
};

export const skillsAPI = {
  getAll: (): Promise<{ data: Skill[] }> =>
    fetchJson<Skill[]>('skills.json'), // api.get('/skills'),
};

export const experienceAPI = {
  getAll: (): Promise<{ data: Experience[] }> =>
    fetchJson<Experience[]>('experience.json'), // api.get('/experience'),

  getById: (id: string): Promise<{ data: Experience }> =>
    fetchJson<Experience[]>('experience.json').then(({ data }) => ({
      data: findById(data, id),
    })), // api.get(`/experience/${id}`)
};

export const educationAPI = {
  getAll: (): Promise<{ data: Education[] }> =>
    fetchJson<Education[]>('education.json'), // api.get('/education'),

  getById: (id: string): Promise<{ data: Education }> =>
    fetchJson<Education[]>('education.json').then(({ data }) => ({
      data: findById(data, id),
    })), // api.get(`/education/${id}`)
};

export const communityAPI = {
  getAll: (): Promise<{ data: CommunityPlatform[] }> =>
    fetchJson<CommunityPlatform[]>('community.json'), // api.get('/community'),
};

export const blogAPI = {
  getAll: (): Promise<{ data: BlogPost[] }> =>
    fetchJson<BlogPost[]>('blog.json'), // api.get('/blog'),

  getById: (id: string): Promise<{ data: BlogPost }> =>
    fetchJson<BlogPost[]>('blog.json').then(({ data }) => ({
      data: findById(data, id),
    })), // api.get(`/blog/${id}`)

  getBySlug: (slug: string): Promise<{ data: BlogPost }> =>
    fetchJson<BlogPost[]>('blog.json').then(({ data }) => ({
      data: findByField(data, 'slug', slug),
    })), // api.get(`/blog/slug/${slug}`)
};

export const certificationsAPI = {
  getAll: (): Promise<{ data: Certification[] }> =>
    fetchJson<Certification[]>('certifications.json'), // api.get('/certifications'),
};

export const testimonialsAPI = {
  getAll: (): Promise<{ data: Testimonial[] }> =>
    fetchJson<Testimonial[]>('testimonials.json'), // api.get('/testimonials'),
};

export const servicesAPI = {
  getAll: (): Promise<{ data: Service[] }> =>
    fetchJson<Service[]>('services.json'), // api.get('/services'),
};

export const resourcesAPI = {
  getAll: (): Promise<{ data: Resource[] }> =>
    fetchJson<Resource[]>('resources.json'), // api.get('/resources'),
};

export const timelineAPI = {
  getAll: (): Promise<{ data: TimelineEvent[] }> =>
    fetchJson<TimelineEvent[]>('timeline.json'), // api.get('/timeline'),
};

export const faqAPI = {
  getAll: (): Promise<{ data: FAQ[] }> =>
    fetchJson<FAQ[]>('faq.json'), // api.get('/faq'),
};

// ==============================
// POST APIs (mocked, no backend)
// ==============================

export const contactAPI = {
  submit: (data: ContactMessage): Promise<{ data: { success: boolean; message: string } }> =>
    Promise.resolve({
      data: {
        success: true,
        message: 'Static mode: not actually sent',
      },
    }), // api.post('/contact', data)
};

// ==============================
// Auth (mocked)
// ==============================

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface AuthResponse {
  user: {
    id: string;
    email: string;
    name: string;
    role?: 'admin' | 'user';
  };
  token?: string;
}

export const authAPI = {
  login: (credentials: LoginCredentials): Promise<{ data: AuthResponse }> =>
    Promise.resolve({
      data: {
        user: {
          id: '1',
          email: credentials.email,
          name: 'Admin User',
          role: 'admin',
        },
        token: 'static-token',
      },
    }), // api.post('/auth/login', credentials)

  logout: (): Promise<{ data: { success: boolean } }> =>
    Promise.resolve({ data: { success: true } }), // api.post('/auth/logout')

  getCurrentUser: (): Promise<{ data: AuthResponse['user'] }> =>
    Promise.resolve({
      data: {
        id: '1',
        email: 'admin@example.com',
        name: 'Admin User',
        role: 'admin',
      },
    }), // api.get('/auth/me')

  refreshToken: (): Promise<{ data: { token: string } }> =>
    Promise.resolve({ data: { token: 'static-token' } }), // api.post('/auth/refresh')
};

export default api;


// import axios, { AxiosInstance } from 'axios';
// import { 
//   Project, 
//   Skill, 
//   ContactMessage, 
//   Experience, 
//   Education,
//   CommunityPlatform,
//   BlogPost,
//   Certification,
//   Testimonial,
//   Service,
//   Resource,
//   TimelineEvent,
//   FAQ
// } from '../types';

// // In production, use relative path; in development, use localhost
// const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// const api: AxiosInstance = axios.create({
//   baseURL: API_BASE_URL,
//   headers: {
//     'Content-Type': 'application/json',
//   },
// });

// export type { 
//   Project, 
//   Skill, 
//   ContactMessage, 
//   Experience, 
//   Education,
//   CommunityPlatform,
//   BlogPost,
//   Certification,
//   Testimonial,
//   Service,
//   Resource,
//   TimelineEvent,
//   FAQ
// };

// export const projectsAPI = {
//   getAll: (): Promise<{ data: Project[] }> => fetchJson<Project[]>("projects.json"), //api.get('/projects'),
//   getById: (id: string): Promise<{ data: Project }> => api.get(`/projects/${id}`),
// };

// export const skillsAPI = {
//   getAll: (): Promise<{ data: Skill[] }> => fetchJson<Skill[]>("skills.json"), //api.get('/skills'),
// };

// export const experienceAPI = {
//   getAll: (): Promise<{ data: Experience[] }> => fetchJson<Experience[]>("experience.json"), //api.get('/experience'),
//   getById: (id: string): Promise<{ data: Experience }> => api.get(`/experience/${id}`),
// };

// export const educationAPI = {
//   getAll: (): Promise<{ data: Education[] }> => fetchJson<Education[]>("education.json"), //api.get('/education'),
//   getById: (id: string): Promise<{ data: Education }> => api.get(`/education/${id}`),
// };

// export const contactAPI = {
//   submit: (data: ContactMessage): Promise<{ data: { success: boolean; message: string } }> => 
//     api.post('/contact', data),
// };

// // Auth API
// export interface LoginCredentials {
//   email: string;
//   password: string;
// }

// export interface AuthResponse {
//   user: {
//     id: string;
//     email: string;
//     name: string;
//     role?: 'admin' | 'user';
//   };
//   token?: string;
// }

// export const authAPI = {
//   login: (credentials: LoginCredentials): Promise<{ data: AuthResponse }> =>
//     api.post('/auth/login', credentials),
//   logout: (): Promise<{ data: { success: boolean } }> =>
//     api.post('/auth/logout'),
//   getCurrentUser: (): Promise<{ data: AuthResponse['user'] }> =>
//     api.get('/auth/me'),
//   refreshToken: (): Promise<{ data: { token: string } }> =>
//     api.post('/auth/refresh'),
// };

// export const communityAPI = {
//   getAll: (): Promise<{ data: CommunityPlatform[] }> => fetchJson<CommunityPlatform[]>("community.json"), //api.get('/community'),
// };

// export const blogAPI = {
//   getAll: (): Promise<{ data: BlogPost[] }> => fetchJson<BlogPost[]>("blog.json"), //api.get('/blog'),
//   getById: (id: string): Promise<{ data: BlogPost }> => api.get(`/blog/${id}`),
//   getBySlug: (slug: string): Promise<{ data: BlogPost }> => api.get(`/blog/slug/${slug}`),
// };

// export const certificationsAPI = {
//   getAll: (): Promise<{ data: Certification[] }> => fetchJson<Certification[]>("certifications.json"), //api.get('/certifications'),
// };

// export const testimonialsAPI = {
//   getAll: (): Promise<{ data: Testimonial[] }> => fetchJson<Testimonial[]>("testimonials.json"), //api.get('/testimonials'),
// };

// export const servicesAPI = {
//   getAll: (): Promise<{ data: Service[] }> => fetchJson<Service[]>("services.json"), //api.get('/services'),
// };

// export const resourcesAPI = {
//   getAll: (): Promise<{ data: Resource[] }> => fetchJson<Resource[]>("resource.json"), //fetchJson<Resource[]>("resource.json") // api.get('/resources'),
// };

// export const timelineAPI = {
//   getAll: (): Promise<{ data: TimelineEvent[] }> => fetchJson<TimelineEvent[]>("timeline.json"), // api.get('/timeline'),
// };

// export const faqAPI = {
//   getAll: (): Promise<{ data: FAQ[] }> => fetchJson<FAQ[]>("faq.json"), //api.get('/faq'),
// };

// export default api;

// const fetchJson = <T>(route: string) : Promise<{data: T}> => {
//   return fetch(`/data/${route}`)
//           .then((res) => res.json())
//           .then((data) => ({ data }));
// }